// components/MUIDatatable/BatchReportsTable.js
import React from "react";
import CustomMUIDataTable from "components/MUIDatatable/CustomMUIDataTablev2";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Chip,
} from "@material-ui/core";

import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";

import AutoRefreshRing from "components/Common/AutoRefreshRing";
import LogsPreview from "components/LogsPreview/LogsPreview";
import TaskCommentsEditor from "components/TaskCommentsEditor/TaskCommentsEditor";
import { dbGet, dbPatch, dbDelete } from "utils/DBFetchers";

const REPORT_STATES_ENDPOINT = "/cli/batch/report_states";
const INITIAL_PAGE_SIZE = 5;

// ----------------------------
// Helpers
// ----------------------------
function safeJsonPretty(val) {
  try {
    return JSON.stringify(val ?? {}, null, 2);
  } catch {
    return String(val ?? "");
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatExecutionTime(seconds) {
  if (seconds === null || seconds === undefined) return "—";

  const s = Number(seconds);
  if (Number.isNaN(s) || s < 0) return "—";

  const hours = Math.floor(s / 3600);
  let minutes = Math.ceil((s % 3600) / 60);

  if (minutes === 60) return `${hours + 1} h`;
  if (hours > 0 && minutes > 0) return `${hours} h ${minutes} min`;
  if (hours > 0) return `${hours} h`;
  return `${Math.max(minutes, 1)} min`;
}

function truncateText(text, max = 30) {
  const s = String(text ?? "").trim();
  if (!s) return "—";
  return s.length > max ? `${s.slice(0, max)}...` : s;
}

// ----------------------------
// Estado -> Chip styles
// ----------------------------
function getJobStateChipProps(stateEs) {
  const s = (stateEs || "").toUpperCase();
  const mapping = {
    CREADO: { bg: "#E0E0E0", fg: "#424242" },
    EJECUTANDO: { bg: "#BBDEFB", fg: "#0D47A1" },
    EXITOSO: { bg: "#C8E6C9", fg: "#1B5E20" },
    FALLIDO: { bg: "#FFCDD2", fg: "#B71C1C" },
    "ERROR INTERNO DEL SERVIDOR": { bg: "#D1C4E9", fg: "#311B92" },
    "ERROR INTERNO DEL CONTENEDOR": { bg: "#FFE0B2", fg: "#E65100" },
    DESCONOCIDO: { bg: "#CFD8DC", fg: "#263238" },
  };
  return mapping[s] || mapping.DESCONOCIDO;
}

function JobStateChip({ stateEs }) {
  const { bg, fg } = getJobStateChipProps(stateEs);
  const label = stateEs || "DESCONOCIDO";
  return (
    <Chip
      label={label}
      size="small"
      style={{
        backgroundColor: bg,
        color: fg,
        fontWeight: 700,
        height: 24,
      }}
    />
  );
}

// ----------------------------
// Modal styles
// ----------------------------
const modalStyles = {
  headerWrap: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 0,
  },
  headerLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    minWidth: 0,
  },
  reportIdMono: {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    color: "rgba(0,0,0,0.7)",
  },

  section: { marginTop: 16 },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 8,
  },
  card: {
    borderRadius: 12,
    background: "#fafafa",
    border: "1px solid rgba(0,0,0,0.08)",
    padding: 12,
  },
  metaRow: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    rowGap: 8,
    columnGap: 14,
  },
  metaLabel: { color: "rgba(0,0,0,0.62)" },

  jsonBox: {
    whiteSpace: "pre-wrap",
    margin: 0,
    padding: 12,
    borderRadius: 12,
    background: "#f3f4f6",
    border: "1px solid rgba(0,0,0,0.08)",
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: 12,
    maxHeight: 200,
    overflow: "auto",
  },

  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#fff",
  },
  fileLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },
  fileName: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 420,
  },
  fileEmpty: { color: "rgba(0,0,0,0.62)" },
};

export default function BatchReportsTable({
  endpointBase = REPORT_STATES_ENDPOINT,
  initialPageSize = INITIAL_PAGE_SIZE,
  queryParams = {},
  onRowClick,
}) {
  // ----------------------------
  // State
  // ----------------------------
  const [tableData, setTableData] = React.useState([]);
  const [rawRows, setRawRows] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialPageSize);

  const [isLoading, setIsLoading] = React.useState(false);

  // Overlay solo para acciones de usuario (cambio de página / page size)
  const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(false);
  const fetchReasonRef = React.useRef("auto"); // "auto" | "user"

  // Modal state
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedReportId, setSelectedReportId] = React.useState(null);

  // Logs (del endpoint /logs)
  const [logsText, setLogsText] = React.useState("");
  const [logStreamUrl, setLogStreamUrl] = React.useState(null);
  const [isLogsLoading, setIsLogsLoading] = React.useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [rowToDelete, setRowToDelete] = React.useState(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const openDetailByReportId = React.useCallback((reportId) => {
    if (!reportId) return;
    setSelectedReportId(reportId);
    setDetailOpen(true);
  }, []);

  const closeDetail = React.useCallback(() => {
    setDetailOpen(false);
    setSelectedReportId(null);
    setLogsText("");
    setLogStreamUrl(null);
    setIsLogsLoading(false);
  }, []);

  // ----------------------------
  // Snackbar
  // ----------------------------
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const snackbarOpenRef = React.useRef(snackbarOpen);
  React.useEffect(() => {
    snackbarOpenRef.current = snackbarOpen;
  }, [snackbarOpen]);

  const copyToClipboard = async (text, okMsg = "Copiado al portapapeles") => {
    try {
      await navigator.clipboard.writeText(text);
      snackbarOpenRef.current("success", okMsg);
    } catch {
      snackbarOpenRef.current("error", "No se pudo copiar al portapapeles");
    }
  };

  // ----------------------------
  // Selected override (description editable)
  // ----------------------------
  const [selectedOverride, setSelectedOverride] = React.useState(null);

  React.useEffect(() => {
    if (!detailOpen) {
      setSelectedOverride(null);
      return;
    }
    setSelectedOverride(null);
  }, [detailOpen, selectedReportId]);

  const selectedComputed = React.useMemo(() => {
    if (!selectedReportId) return null;
    const base = (rawRows || []).find((r) => r?.report_id === selectedReportId) ?? null;
    if (!base) return null;
    return selectedOverride ? { ...base, ...selectedOverride } : base;
  }, [rawRows, selectedReportId, selectedOverride]);

  // ----------------------------
  // Delete row handling
  // ----------------------------

  const handleDeleteClick = (e, row) => {
    e.preventDefault();
    e.stopPropagation();
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
    setIsDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    setIsDeleting(true);

    try {
      await dbDelete(`${endpointBase}/${rowToDelete.report_id}`);
      snackbarOpenRef.current("success", "Reporte eliminado correctamente");
      triggerRefreshStable();
    } catch (error) {
      console.error("Error deleting report:", error?.message || "Unknown error");
      snackbarOpenRef.current("error", "Error al eliminar el reporte");
    } finally {
      handleCloseDelete();
    }
  };

  // ----------------------------
  // File helpers
  // ----------------------------
  const openUrl = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderFileRow = ({ filename, url, emptyText }) => {
    if (!filename && !url) {
      return (
        <div style={modalStyles.fileRow}>
          <div style={modalStyles.fileLeft}>
            <InsertDriveFileOutlinedIcon style={{ fontSize: 34, opacity: 0.35 }} />
            <Typography variant="body2" style={modalStyles.fileEmpty}>
              {emptyText}
            </Typography>
          </div>
        </div>
      );
    }

    return (
      <div style={modalStyles.fileRow}>
        <div style={modalStyles.fileLeft}>
          <InsertDriveFileOutlinedIcon style={{ fontSize: 34, opacity: 0.75 }} />
          <div style={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              style={modalStyles.fileName}
              title={filename || url || ""}
            >
              {filename || "archivo"}
            </Typography>
            <Typography
              variant="caption"
              style={{
                color: "rgba(0,0,0,0.62)",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 420,
              }}
              title={url || ""}
            >
              {url || "—"}
            </Typography>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openUrl(url)}
            disabled={!url}
            style={{ textTransform: "none" }}
          >
            Descargar
          </Button>
          <Tooltip title="Copiar URL">
            <span>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(url || "", "URL copiada")}
                disabled={!url}
              >
                <FileCopyOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
    );
  };

  // ----------------------------
  // Columns
  // ----------------------------
  const COLS = React.useMemo(
    () => [
      {
        key: "__detail__",
        label: "",
        format: (_raw, row) => (
          <div style={{ display: "flex", gap: 0 }}>
            <Tooltip title="Ver detalle">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openDetailByReportId(row?.report_id);
                }}
                aria-label="Ver detalle"
              >
                <ContentPasteSearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, row)}
                aria-label="Eliminar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", width: 90 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        key: "report_id",
        label: "id",
        format: (raw) => raw ?? "",
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 150 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap", fontFamily: "monospace" } }),
        },
      },
      {
        key: "cmd_name",
        label: "Tarea",
        format: (raw) => raw ?? "",
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 180 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        key: "job_state_es",
        label: "Estado",
        format: (raw) => <JobStateChip stateEs={raw} />,
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 140 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        key: "created_at",
        label: "Iniciado",
        format: (raw) => formatDateTime(raw),
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 160 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        key: "ended_at",
        label: "Finalizado",
        format: (raw) => formatDateTime(raw),
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 160 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        key: "execution_time_in_seconds",
        label: "Duración",
        format: (raw) => formatExecutionTime(raw),
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 170 } }),
          setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
        },
      },
      {
        key: "description",
        label: "Observaciones",
        format: (raw) => {
          const full = String(raw ?? "").trim();
          const short = truncateText(full, 30);
          if (full && full.length > 30) {
            return (
              <Tooltip title={full}>
                <span>{short}</span>
              </Tooltip>
            );
          }
          return short || "—";
        },
        options: {
          sort: false,
          setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 220 } }),
          setCellProps: () => ({
            style: {
              whiteSpace: "nowrap",
              maxWidth: 260,
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }),
        },
      },
    ],
    [openDetailByReportId]
  );

  const columns = React.useMemo(
    () =>
      COLS.map(({ label, options }) => ({
        name: label,
        options,
      })),
    [COLS]
  );

  // ----------------------------
  // Fetch (tabla)
  // ----------------------------
  const makeQueryParams = React.useCallback(() => {
    const params = new URLSearchParams();
    params.append("page", String(page + 1));
    params.append("page_size", String(rowsPerPage));
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    return params;
  }, [page, queryParams, rowsPerPage]);

  const fillTable = React.useCallback(
    (apiPayload) => {
      const { total_pages, total_items, report_states = [] } = apiPayload || {};
      if (typeof total_pages === "number") setTotalPages(total_pages);
      if (typeof total_items === "number") setCount(total_items);

      const safeStates = Array.isArray(report_states) ? report_states : [];
      setRawRows(safeStates);

      const rows = safeStates.map((row) =>
        COLS.map((col) => {
          if (col.key === "__detail__") return col.format(null, row);
          const raw = row?.[col.key];
          return col.format ? col.format(raw, row) : raw ?? "";
        })
      );

      setTableData(rows);
    },
    [COLS]
  );

  const requestSeq = React.useRef(0);

  const fetchPage = React.useCallback(() => {
    const seq = ++requestSeq.current;
    const params = makeQueryParams();
    setIsLoading(true);

    dbGet(`${endpointBase}?${params.toString()}`)
      .then((data) => {
        if (seq !== requestSeq.current) return;
        fillTable(data);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        snackbarOpenRef.current("error", "Error al cargar reportes batch");
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;

        setIsLoading(false);

        if (fetchReasonRef.current === "user") {
          setShowLoadingOverlay(false);
          fetchReasonRef.current = "auto";
        }
      });
  }, [makeQueryParams, endpointBase, fillTable]);

  // ----------------------------
  // Fetch (logs del modal)
  // ----------------------------
  const logsRequestSeq = React.useRef(0);

  const fetchLogs = React.useCallback(
    (reportId) => {
      if (!reportId) return;

      const seq = ++logsRequestSeq.current;
      setIsLogsLoading(true);

      dbGet(`${endpointBase}/${reportId}/logs`)
        .then((data) => {
          if (seq !== logsRequestSeq.current) return;

          setLogsText(String(data?.logs ?? ""));
          setLogStreamUrl(data?.log_stream_url || null);
        })
        .catch(() => {
          if (seq !== logsRequestSeq.current) return;
          snackbarOpenRef.current("error", "Error al cargar logs del reporte");
        })
        .finally(() => {
          if (seq !== logsRequestSeq.current) return;
          setIsLogsLoading(false);
        });
    },
    [endpointBase]
  );

  // Al abrir modal o cambiar report_id seleccionado: traer logs inmediatamente
  React.useEffect(() => {
    if (!detailOpen || !selectedReportId) return;

    setLogsText("");
    setLogStreamUrl(null);
    fetchLogs(selectedReportId);
  }, [detailOpen, selectedReportId, fetchLogs]);

  // refs para auto-refresh estable
  const isLoadingRef = React.useRef(isLoading);
  React.useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const fetchPageRef = React.useRef(fetchPage);
  React.useEffect(() => {
    fetchPageRef.current = fetchPage;
  }, [fetchPage]);

  const fetchLogsRef = React.useRef(fetchLogs);
  React.useEffect(() => {
    fetchLogsRef.current = fetchLogs;
  }, [fetchLogs]);

  const detailOpenRef = React.useRef(detailOpen);
  React.useEffect(() => {
    detailOpenRef.current = detailOpen;
  }, [detailOpen]);

  const selectedReportIdRef = React.useRef(selectedReportId);
  React.useEffect(() => {
    selectedReportIdRef.current = selectedReportId;
  }, [selectedReportId]);

  const triggerRefreshStable = React.useCallback(() => {
    if (isLoadingRef.current) return;

    // 1) Siempre refrescar la tabla
    fetchReasonRef.current = "auto";
    fetchPageRef.current();

    // 2) Si hay modal abierto, refrescar además los logs del report_id abierto
    if (detailOpenRef.current && selectedReportIdRef.current) {
      fetchLogsRef.current(selectedReportIdRef.current);
    }
  }, []);

  // ✅ Fetch inicial: con overlay
  const didInitialFetchRef = React.useRef(false);
  React.useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;

    fetchReasonRef.current = "user";
    setShowLoadingOverlay(true);
    fetchPage();
  }, [fetchPage]);

  // ✅ Fetch automático al cambiar page / rowsPerPage (acción del usuario)
  React.useEffect(() => {
    if (!didInitialFetchRef.current) return;
    if (fetchReasonRef.current !== "user") return;
    fetchPage();
  }, [page, rowsPerPage, fetchPage]);

  // ----------------------------
  // Paginación: dispara fetch inmediatamente (y overlay)
  // ----------------------------
  const changePage = (newPage) => {
    fetchReasonRef.current = "user";
    setShowLoadingOverlay(true);
    setPage(newPage);
  };

  const changeRowsPerPage = (newRows) => {
    fetchReasonRef.current = "user";
    setShowLoadingOverlay(true);
    setRowsPerPage(newRows);
    setPage(0);
  };

  // ----------------------------
  // PATCH description
  // ----------------------------
  const saveDescription = React.useCallback(
    async (newDescription) => {
      if (!selectedReportId) return;

      setSelectedOverride((prev) => ({
        ...(prev || {}),
        description: newDescription,
      }));

      try {
        const endpoint = `/cli/batch/report_states/${selectedReportId}/description`;
        const resp = await dbPatch(endpoint, { description: newDescription });

        setSelectedOverride((prev) => ({
          ...(prev || {}),
          description: resp?.description ?? newDescription,
        }));

        snackbarOpenRef.current("success", "Observación actualizada");
      } catch (e) {
        const base = (rawRows || []).find((r) => r?.report_id === selectedReportId);
        setSelectedOverride((prev) => ({
          ...(prev || {}),
          description: base?.description ?? "",
        }));

        snackbarOpenRef.current("error", "No se pudo actualizar la observación");
        throw e;
      }
    },
    [selectedReportId, rawRows]
  );

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />

      <CustomMUIDataTable
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            <span>Progreso de Tareas</span>
          </div>
        }
        data={tableData}
        columns={columns}
        count={count}
        page={page}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        changePage={changePage}
        changeRowsPerPage={changeRowsPerPage}
        options={{
          pagination: true,
          isLoading,
          filter: false,
          search: false,
          download: false,
          onDownload: null,

          // ✅ overlay solo cuando el fetch viene de usuario
          disableLoadingOverlay: !showLoadingOverlay,

          customToolbar: () => (
            <div style={{ display: "inline-flex", alignItems: "center" }}>
              <AutoRefreshRing
                seconds={5}
                isLoading={isLoading}
                onTickComplete={triggerRefreshStable}
                tooltip="Auto-refresh cada 5 segundos"
              />
            </div>
          ),
          onRowClick: onRowClick ? (rowData) => onRowClick(rowData) : undefined,
        }}
      />

      {/* Modal Detalle */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>
          <div style={modalStyles.headerWrap}>
            <div style={modalStyles.headerLeft}>
              <Typography variant="h6" style={{ lineHeight: 1.2 }}>
                Detalle de ejecución
              </Typography>

              <div style={modalStyles.headerLine}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>
                  {selectedComputed?.cmd_name || "—"}
                </Typography>

                {selectedComputed?.job_state_es ? (
                  <JobStateChip stateEs={selectedComputed.job_state_es} />
                ) : null}

                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <Typography variant="body2" style={modalStyles.reportIdMono}>
                    {selectedComputed?.report_id || "—"}
                  </Typography>
                  <Tooltip title="Copiar Report ID">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyToClipboard(
                            String(selectedComputed?.report_id || ""),
                            "Report ID copiado"
                          )
                        }
                        disabled={!selectedComputed?.report_id}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <Tooltip title="Cerrar">
                <IconButton onClick={closeDetail} aria-label="Cerrar">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>

        <DialogContent dividers>
          {!selectedComputed ? (
            <Typography variant="body2">Sin información para mostrar.</Typography>
          ) : (
            <>
              <div style={modalStyles.card}>
                <div style={modalStyles.metaRow}>
                  <Typography variant="body2" style={modalStyles.metaLabel}>
                    Iniciado
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(selectedComputed?.created_at)}
                  </Typography>

                  <Typography variant="body2" style={modalStyles.metaLabel}>
                    Finalizado
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(selectedComputed?.ended_at)}
                  </Typography>

                  <Typography variant="body2" style={modalStyles.metaLabel}>
                    Duración
                  </Typography>
                  <Typography variant="body2">
                    {formatExecutionTime(selectedComputed?.execution_time_in_seconds)}
                  </Typography>

                  <Typography variant="body2" style={modalStyles.metaLabel}>
                    Última actualización
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(selectedComputed?.updated_at)}
                  </Typography>
                </div>
              </div>

              <Divider style={{ margin: "18px 0" }} />

              <div style={modalStyles.sectionTitleRow}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <Typography variant="subtitle2">Logs</Typography>
                </div>

                <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  {logStreamUrl ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openUrl(logStreamUrl)}
                      style={{ textTransform: "none" }}
                    >
                      Logs completos
                    </Button>
                  ) : null}

                  <Tooltip title="Copiar logs">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(String(logsText || ""), "Logs copiados")}
                        disabled={!logsText}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </div>

              {/* Importante: LogsPreview sigue usando prop "stdout", pero ahora le pasamos logsText */}
              <LogsPreview stdout={logsText} height={340} />

              <Divider style={{ margin: "18px 0" }} />

              <div style={modalStyles.section}>
                <TaskCommentsEditor
                  label="Observación"
                  value={selectedComputed?.description || ""}
                  maxChars={2000}
                  onSave={saveDescription}
                />
              </div>
              <Divider style={{ margin: "18px 0" }} />
              {/* Archivos */}
              <div style={modalStyles.section}>
                <div style={modalStyles.sectionTitleRow}>
                  <Typography variant="subtitle2">Archivo de entrada</Typography>
                </div>
                {renderFileRow({
                  filename: selectedComputed.input_filename,
                  url: selectedComputed.input_file_url,
                  emptyText: "(sin archivo de entrada)",
                })}

                <div style={{ marginTop: 14 }}>
                  <div style={modalStyles.sectionTitleRow}>
                    <Typography variant="subtitle2">Archivos de salida</Typography>
                  </div>

                  {(() => {
                    const names = Array.isArray(selectedComputed.output_filenames)
                      ? selectedComputed.output_filenames
                      : [];
                    const urls = Array.isArray(selectedComputed.output_files_urls)
                      ? selectedComputed.output_files_urls
                      : [];
                    const max = Math.max(names.length, urls.length);

                    if (max === 0) {
                      return (
                        <div style={modalStyles.fileRow}>
                          <div style={modalStyles.fileLeft}>
                            <InsertDriveFileOutlinedIcon style={{ fontSize: 34, opacity: 0.35 }} />
                            <Typography variant="body2" style={modalStyles.fileEmpty}>
                              (sin archivos de salida)
                            </Typography>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {Array.from({ length: max }).map((_, i) =>
                          renderFileRow({
                            filename: names[i],
                            url: urls[i],
                            emptyText: "(sin archivo)",
                          })
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <Divider style={{ margin: "18px 0" }} />
              {/* Parámetros */}
              <div style={modalStyles.section}>
                <div style={modalStyles.sectionTitleRow}>
                  <Typography variant="subtitle2">Parámetros</Typography>
                  <Tooltip title="Copiar cmd_input">
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(
                          safeJsonPretty(selectedComputed.cmd_input),
                          "cmd_input copiado"
                        )
                      }
                    >
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <pre style={modalStyles.jsonBox}>{safeJsonPretty(selectedComputed.cmd_input)}</pre>
              </div>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDetail} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación Eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar el reporte{" "}
            <b>
              {rowToDelete?.cmd_name} ({rowToDelete?.report_id})
            </b>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} color="primary" disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
